import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, RefreshCw, Server, Database, Wifi, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { API_BASE_URL } from '@/config/api';

const API = API_BASE_URL;

export default function StatusPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/`);
      const data = await response.json();
      
      setStatus({
        api: response.ok,
        database: data.status === 'healthy',
        message: data.message || 'Chain29 API'
      });
    } catch (error) {
      setStatus({
        api: false,
        database: false,
        message: 'Unable to connect'
      });
    } finally {
      setLoading(false);
      setLastChecked(new Date().toLocaleTimeString());
    }
  };

  useEffect(() => {
    checkStatus();
    // Refresh every 30 seconds
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const StatusIcon = ({ ok }) => {
    if (ok === null) return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    return ok ? (
      <CheckCircle className="h-5 w-5 text-green-500" />
    ) : (
      <XCircle className="h-5 w-5 text-red-500" />
    );
  };

  const services = [
    { name: 'API Server', icon: Server, status: status?.api },
    { name: 'Database', icon: Database, status: status?.database },
    { name: 'Authentication', icon: Shield, status: status?.api },
    { name: 'Network', icon: Wifi, status: status?.api },
  ];

  const allOperational = status?.api && status?.database;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">System Status</h1>
          <p className="text-slate-400">Chain29 Restaurant Back Office</p>
        </div>

        {/* Overall Status */}
        <Card className="border-slate-800/60 bg-slate-900/50 mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center gap-3">
              {loading ? (
                <RefreshCw className="h-8 w-8 text-blue-500 animate-spin" />
              ) : allOperational ? (
                <CheckCircle className="h-8 w-8 text-green-500" />
              ) : (
                <XCircle className="h-8 w-8 text-red-500" />
              )}
              <span className="text-xl font-semibold text-white">
                {loading ? 'Checking...' : allOperational ? 'All Systems Operational' : 'Some Systems Degraded'}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Services Grid */}
        <Card className="border-slate-800/60 bg-slate-900/50 mb-6">
          <CardHeader>
            <CardTitle className="text-white">Services</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {services.map((service) => (
              <div key={service.name} className="flex items-center justify-between p-3 rounded-lg bg-slate-950/50">
                <div className="flex items-center gap-3">
                  <service.icon className="h-5 w-5 text-slate-400" />
                  <span className="text-slate-200">{service.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${service.status ? 'text-green-400' : 'text-red-400'}`}>
                    {loading ? 'Checking' : service.status ? 'Operational' : 'Down'}
                  </span>
                  <StatusIcon ok={loading ? null : service.status} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center space-y-4">
          <Button
            onClick={checkStatus}
            disabled={loading}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh Status
          </Button>
          
          {lastChecked && (
            <p className="text-xs text-slate-500">
              Last checked: {lastChecked}
            </p>
          )}

          <p className="text-xs text-slate-600 mt-4">
            For support, contact support@asterpos.com
          </p>
        </div>
      </div>
    </div>
  );
}
